// Several browser tabs on one IndexedDB (docs/SYNC.md §2 "Several tabs"). The store only needs the
// coordinator; which tab runs the sync engine is the app's choice (electLeader).

export interface TabMessage { gen: number }

export interface TabCoordinator {
  /** Run fn while no other tab is committing to this database. */
  lock<T>(fn: () => Promise<T>): Promise<T>
  /** Tell the other tabs a commit landed (a hint — they also catch up before their own writes). */
  publish(msg: TabMessage): void
  subscribe(fn: (msg: TabMessage) => void): () => void
  close(): void
}

interface LockManagerLike { request<T>(name: string, fn: () => Promise<T>): Promise<T>; request<T>(name: string, opts: object, fn: () => Promise<T>): Promise<T> }
interface ChannelLike { postMessage(m: unknown): void; close(): void; onmessage: ((e: { data: unknown }) => void) | null }

const locksOf = (): LockManagerLike | null => (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator?.locks ?? null
const channelCtor = (): (new (name: string) => ChannelLike) | null =>
  (globalThis as { BroadcastChannel?: new (name: string) => ChannelLike }).BroadcastChannel ?? null

/** Web Locks + BroadcastChannel for the database `name`; null where the browser has neither (then: one tab). */
export function webTabs(name: string): TabCoordinator | null {
  const locks = locksOf()
  const Channel = channelCtor()
  if (!locks || !Channel) return null
  const ch = new Channel(`battleops-tabs:${name}`)
  const subs = new Set<(m: TabMessage) => void>()
  ch.onmessage = (e) => { for (const fn of [...subs]) { try { fn(e.data as TabMessage) } catch { /* listener */ } } }
  return {
    lock: (fn) => locks.request(`battleops-write:${name}`, fn),
    publish: (msg) => ch.postMessage(msg),
    subscribe: (fn) => { subs.add(fn); return () => { subs.delete(fn) } },
    close: () => ch.close(),
  }
}

/**
 * Hold the `battleops-sync:<name>` lock for as long as this tab lives: `onLead` runs once this tab gets it
 * (straight away for the first tab; when the leader closes, for the next one waiting). Returns a function that
 * gives the lead up (sign-out). No Web Locks = this tab leads.
 */
export function electLeader(name: string, onLead: () => void): () => void {
  const locks = locksOf()
  if (!locks) { onLead(); return () => {} }
  let release: (() => void) | null = null
  let cancelled = false
  locks.request(`battleops-sync:${name}`, () => new Promise<void>((resolve) => {
    if (cancelled) { resolve(); return }
    release = resolve
    onLead()
  })).catch(() => undefined)
  return () => { cancelled = true; release?.() }
}
